import React, { useEffect, useState } from 'react';
import Leaderboard from '../Leaderboard';
import { statusClass, formatMana, MEDALS } from './format.js';

function MarketMeta({ market }) {
  const isBinary = market.outcomeType === 'BINARY';
  return (
    <div className="market-meta">
      {market.isResolved && market.resolution ? (
        <span className={`outcome-pill outcome-${market.resolution === 'YES' ? 'yes' : market.resolution === 'NO' ? 'no' : 'other'}`}>
          {market.resolution === 'MKT' ? 'RESOLVED' : market.resolution}
        </span>
      ) : isBinary && typeof market.probability === 'number' ? (
        <span className="prob-pill">{Math.round(market.probability * 100)}%</span>
      ) : null}
      {typeof market.uniqueBettorCount === 'number' && (
        <span className="market-stat" title="Traders">👥 {market.uniqueBettorCount}</span>
      )}
      {typeof market.volume24Hours === 'number' && market.volume24Hours > 0 && (
        <span className="market-stat" title="24h volume">{formatMana(Math.round(market.volume24Hours))} today</span>
      )}
    </div>
  );
}

export default function TournamentPage({ id, onBack, onAdmin }) {
  const [tournament, setTournament] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const [marketError, setMarketError] = useState('');
  const [joinUsername, setJoinUsername] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState('');

  const loadTournament = () => {
    fetch(`/api/tournaments/${id}`)
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(setTournament)
      .catch(() => setNotFound(true));
  };

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      return;
    }
    loadTournament();
  }, [id]);

  const join = async () => {
    setJoining(true);
    setJoinError('');
    setJoinSuccess('');
    try {
      const res = await fetch(`/api/tournaments/${id}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: joinUsername.trim(), joinCode: joinCode.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setJoinError(data.error || 'Failed to join');
        return;
      }
      setJoinSuccess(
        data.alreadyJoined
          ? `${data.userName} is already registered for this tournament.`
          : `You're in! ${data.userName} has been added to the participants.`
      );
      setJoinUsername('');
      loadTournament();
    } catch (err) {
      setJoinError('Network error occurred');
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setMarkets([]);
    setMarketError('');
    setLoadingMarkets(true);

    fetch(`/api/tournaments/${id}/markets`)
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: 'Failed to load markets' }));
          throw err;
        }
        return r.json();
      })
      .then(infos =>
        Promise.all(
          infos.map(info =>
            fetch(`/api/manifold/market/${info.id}`)
              .then(r => r.json())
              .then(market => ({ ...market, _info: info }))
              .catch(() => ({ id: info.id, question: info.id, error: true, _info: info }))
          )
        )
      )
      .then(loaded => {
        // Brief: markets ordered by activity.
        loaded.sort((a, b) => (b.volume24Hours || 0) - (a.volume24Hours || 0));
        setMarkets(loaded);
      })
      .catch(err => {
        console.error('Error loading tournament markets', err);
        if (err.breakdown) {
          const parts = Object.entries(err.breakdown)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ');
          setMarketError(`${err.error || err.message} - ${parts}`);
        } else {
          setMarketError(err.error || err.message || 'Failed to load markets');
        }
      })
      .finally(() => setLoadingMarkets(false));
  }, [id]);

  if (notFound) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Tournament not found</h1>
          <p className="page-subtitle">This tournament may have been deleted.</p>
        </div>
        <button className="btn btn-secondary" onClick={onBack}>← All tournaments</button>
      </div>
    );
  }

  if (!tournament) {
    return <div className="loading">Loading tournament...</div>;
  }

  const prizes = tournament.prizes || {};
  const hasPrizes = prizes.first || prizes.second || prizes.third;
  const isFinished = tournament.status === 'Finished';
  const winners = tournament.winners || [];
  const prizeForRank = { 1: prizes.first, 2: prizes.second, 3: prizes.third };

  return (
    <div>
      <div className="tournament-actions">
        <button className="btn btn-secondary" onClick={onBack}>
          ← Back
        </button>
        <button className="btn btn-secondary" onClick={() => onAdmin(id)}>
          Admin
        </button>
      </div>

      <div className="page-header">
        <div className="tournament-title-row">
          <h1 className="page-title">{tournament.name}</h1>
          <span className={`status-pill ${statusClass(tournament.status)}`}>
            {tournament.status}
          </span>
          {tournament.participantsOnly && (
            <span className="status-pill invitational-pill">Invitational</span>
          )}
        </div>
        {tournament.description && (
          <p className="page-subtitle description-text">{tournament.description}</p>
        )}
      </div>

      <div className="tournament-grid">
        <div>
          {isFinished && winners.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">🏆 Final Results</h3>
              </div>
              <div className="winners-list">
                {winners.map(w => (
                  <div key={w.rank} className={`winner-row winner-rank-${w.rank}`}>
                    <span className="winner-medal">{MEDALS[w.rank] || `#${w.rank}`}</span>
                    {w.avatarUrl && <img src={w.avatarUrl} alt="" className="avatar" />}
                    <a
                      href={`/user.html?u=${encodeURIComponent(w.username || w.userId)}`}
                      className="winner-name"
                    >
                      {w.userName}
                    </a>
                    {prizeForRank[w.rank] && (
                      <span className="winner-prize">{formatMana(prizeForRank[w.rank])}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{isFinished ? 'Leaderboard (live)' : 'Leaderboard'}</h3>
              {isFinished && (
                <p className="card-subtitle">
                  Standings keep updating as markets resolve; final results above were locked in when the tournament finished.
                </p>
              )}
              {tournament.participantsOnly && (
                <p className="card-subtitle">
                  Invitational — only listed participants are ranked.
                </p>
              )}
            </div>
            <Leaderboard tournamentId={id} />
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Markets</h3>
              {markets.length > 1 && <p className="card-subtitle">Ordered by recent activity</p>}
            </div>
            {loadingMarkets ? (
              <div className="center-message">Loading markets...</div>
            ) : marketError ? (
              <div className="center-message error-text">{marketError}</div>
            ) : markets.length > 0 ? (
              <div className="markets-list">
                {markets.map((market, index) => (
                  <div key={market.id || index} className="market-item">
                    <div className="market-item-main">
                      {market.error ? (
                        <div className="market-error">{market.question} (failed to load)</div>
                      ) : (
                        <a
                          href={`https://manifold.markets/${market.creatorUsername}/${market.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="market-link"
                        >
                          {market.question}
                        </a>
                      )}
                      {!market.error && <MarketMeta market={market} />}
                    </div>
                    <div className="tags-list market-item-tags">
                      {(market._info.tags || []).map(tag => (
                        <span key={tag} className="tag-chip">{tag}</span>
                      ))}
                      {market._info.manual && (
                        <span className="tag-chip tag-chip-manual">hand-picked</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="center-message">No markets added yet</div>
            )}
          </div>
        </div>

        <div>
          {tournament.participantsOnly && tournament.status === 'Coming Up' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Join this tournament</h3>
                <p className="card-subtitle">
                  Signups are open until the tournament starts.
                  {tournament.joinRequired && ' A join code from the host is required.'}
                </p>
              </div>
              <div className="join-form">
                <input
                  className="form-input"
                  placeholder="Your Manifold username"
                  value={joinUsername}
                  onChange={e => setJoinUsername(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && joinUsername.trim() && join()}
                />
                {tournament.joinRequired && (
                  <input
                    className="form-input"
                    placeholder="Join code"
                    value={joinCode}
                    onChange={e => setJoinCode(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && joinUsername.trim() && join()}
                  />
                )}
                <button
                  className="btn btn-primary"
                  onClick={join}
                  disabled={joining || !joinUsername.trim() || (tournament.joinRequired && !joinCode.trim())}
                >
                  {joining ? 'Joining...' : 'Join'}
                </button>
                {joinError && <div className="error-text">{joinError}</div>}
                {joinSuccess && <div className="join-success">{joinSuccess}</div>}
              </div>
            </div>
          )}

          {hasPrizes && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Prizes</h3>
              </div>
              <div className="prizes-list">
                {prizes.first && (
                  <div className="prize-row"><span className="winner-medal">🥇</span> <span className="prize-amount">{formatMana(prizes.first)}</span></div>
                )}
                {prizes.second && (
                  <div className="prize-row"><span className="winner-medal">🥈</span> <span className="prize-amount">{formatMana(prizes.second)}</span></div>
                )}
                {prizes.third && (
                  <div className="prize-row"><span className="winner-medal">🥉</span> <span className="prize-amount">{formatMana(prizes.third)}</span></div>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Tournament Info</h3>
            </div>
            <div className="info-section">
              {tournament.hosts && tournament.hosts.length > 0 && (
                <div>
                  <div className="info-label">Hosts</div>
                  <ul className="hosts-list">
                    {tournament.hosts.map(host => (
                      <li key={host.userId} className="host-row">
                        {host.avatarUrl && <img src={host.avatarUrl} alt="" className="avatar avatar-sm" />}
                        <a
                          href={`/user.html?u=${encodeURIComponent(host.username || host.userId)}`}
                          className="host-link"
                        >
                          {host.userName}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tournament.participants && tournament.participants.length > 0 && (
                <div>
                  <div className="info-label">
                    Participants ({tournament.participants.length})
                  </div>
                  <ul className="hosts-list">
                    {tournament.participants.map(p => (
                      <li key={p.userId} className="host-row">
                        {p.avatarUrl && <img src={p.avatarUrl} alt="" className="avatar avatar-sm" />}
                        <a
                          href={`/user.html?u=${encodeURIComponent(p.username || p.userId)}`}
                          className="host-link"
                        >
                          {p.userName}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tournament.tags && tournament.tags.length > 0 && (
                <div>
                  <div className="info-label">Topics</div>
                  <div className="tags-list">
                    {tournament.tags.map(tag => (
                      <a
                        key={tag}
                        className="tag-chip tag-chip-link"
                        href={'https://manifold.markets/topic/' + encodeURIComponent(tag)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {tag}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {(!tournament.hosts || tournament.hosts.length === 0) &&
                (!tournament.participants || tournament.participants.length === 0) &&
                (!tournament.tags || tournament.tags.length === 0) && (
                  <div className="muted">No additional info</div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
